import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { cn, formatDate, formatCurrency } from '@/lib/utils';
import { FileSpreadsheet } from 'lucide-react';

const quoteStatusColors: Record<string, string> = {
  draft: 'bg-gray-500/10 text-gray-400',
  sent: 'bg-blue-500/10 text-blue-400',
  approved: 'bg-yellow-500/10 text-yellow-400',
  revisions_requested: 'bg-orange-500/10 text-orange-400',
  accepted: 'bg-dyndev-green/10 text-dyndev-green',
  converted: 'bg-purple-500/10 text-purple-400',
  expired: 'bg-red-500/10 text-red-400',
};

const quoteStatusLabels: Record<string, string> = {
  draft: 'Draft',
  sent: 'Sent',
  approved: 'Approved',
  revisions_requested: 'Changes Requested',
  accepted: 'Accepted',
  converted: 'Converted',
  expired: 'Expired',
};

interface ProjectQuotesTabProps {
  quotes: any[];
  quotesLoading: boolean;
}

export function ProjectQuotesTab({
  quotes,
  quotesLoading,
}: ProjectQuotesTabProps) {
  const navigate = useNavigate();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-[var(--text-secondary)]">
          {quotes.length} quote{quotes.length !== 1 ? 's' : ''}
        </h3>
      </div>

      {quotesLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="h-24 rounded-lg bg-[var(--border-color)] animate-pulse"
            />
          ))}
        </div>
      ) : quotes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <FileSpreadsheet
            size={32}
            className="text-[var(--text-secondary)] mb-3"
          />
          <p className="text-sm font-medium mb-1">
            No quotes for this project
          </p>
          <p className="text-xs text-[var(--text-secondary)]">
            No quotes linked to this project yet. Quotes are managed
            from the Quotes page.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {quotes.map((quote: any) => {
            const quoteTotal =
              quote.lineItems?.reduce(
                (s: number, li: any) => s + li.total,
                0
              ) || 0;
            return (
              <div
                key={quote.id}
                className="flex items-center gap-4 p-3 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] hover:border-dyndev-green/20 transition-colors cursor-pointer"
                onClick={() => navigate('/quotes')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') navigate('/quotes');
                }}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-medium">
                      {quote.quoteNumber}
                    </span>
                    <Badge
                      variant="outline"
                      className={cn(
                        'text-[10px] px-1.5 py-0',
                        quoteStatusColors[quote.status]
                      )}
                    >
                      {quoteStatusLabels[quote.status] ||
                        quote.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-[var(--text-secondary)]">
                    <span>
                      {quote.lead?.companyName ||
                        quote.lead?.contactName ||
                        'Unknown'}
                    </span>
                    {quote.validUntil && (
                      <span>
                        Valid until: {formatDate(quote.validUntil)}
                      </span>
                    )}
                    {quote.notes && (
                      <span className="truncate max-w-[200px]">
                        {quote.notes}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-bold">
                    {formatCurrency(quoteTotal)}
                  </p>
                  {quote.lineItems?.length > 0 && (
                    <p className="text-[10px] text-[var(--text-secondary)]">
                      {quote.lineItems.length} item
                      {quote.lineItems.length !== 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
